import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import privateicon from "../image/privateicon.png";
import publicicon from "../image/publicicon.png";
import LocationPicker from "../utils/LocationPicker";
import { ensureValidToken, isOfflineMode } from "../utils/authService";
import "../styles/AddNewCard2.css";

const AddNewCard2 = ({ editMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [socialLinks, setSocialLinks] = useState(["", "", ""]);
  const [companyWebsite, setCompanyWebsite] = useState(""); // New state for company website
  const [selectedCardType, setSelectedCardType] = useState("private");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const location = useLocation();

  const [formData, setFormData] = useState({
    full_name: "",
    designation: "",
    company_name: "",
    phone_number: "",
    email: "",
    location_details: "",
    profile_image: null,
  });

  const socialMediaIcons = [
    { name: "whatsapp", iconClass: "fab fa-whatsapp" },
    { name: "instagram", iconClass: "fab fa-instagram" },
    { name: "linkedin", iconClass: "fab fa-linkedin" },
    { name: "twitter", iconClass: "fab fa-twitter" },
    { name: "github", iconClass: "fab fa-github" },
    { name: "facebook", iconClass: "fab fa-facebook" },
    { name: "location", iconClass: "fas fa-map-marker-alt" },
    { name: "website", iconClass: "fas fa-globe" }, // Added website icon
  ];

  // Function to handle location selection
  const handleLocationSelect = (mapsUrl) => {
    // Add the Google Maps URL to social links
    const newSocialLinks = [...socialLinks];
    const locationIndex = newSocialLinks.findIndex(link => 
      link.includes('google.com/maps') || link.includes('maps.app.goo.gl')
    );
    
    if (locationIndex !== -1) {
      // Replace existing location
      newSocialLinks[locationIndex] = mapsUrl;
    } else {
      // Add new location if there's an empty slot
      const emptyIndex = newSocialLinks.findIndex(link => link.trim() === '');
      if (emptyIndex !== -1) {
        newSocialLinks[emptyIndex] = mapsUrl;
      } else {
        // Add to the end if no empty slots
        newSocialLinks.push(mapsUrl);
      }
    }
    
    setSocialLinks(newSocialLinks);
    setShowLocationPicker(false);
    alert("Location saved successfully!");
  };

  // Function to detect if a link is a location link
  const isLocationLink = (link) => {
    return link.includes('google.com/maps') || link.includes('maps.app.goo.gl');
  };

  // Function to detect if a link is a website link
  const isWebsiteLink = (link) => {
    return link === companyWebsite && companyWebsite.trim() !== "";
  };

  // Load existing card data in edit mode
  useEffect(() => {
    if (editMode && id && !initialDataLoaded) {
      const fetchCardDetails = async () => {
        // Skip API calls when offline
        if (isOfflineMode()) {
          return;
        }

        try {
          const accessToken = await ensureValidToken();
          if (!accessToken) {
            navigate("/login");
            return;
          }
          const response = await axios.get(
            `https://api.nexcard.co.in/api/cards/${id}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          
          setFormData({
            full_name: response.data.full_name || "",
            designation: response.data.designation || "",
            company_name: response.data.company_name || "",
            phone_number: response.data.phone_number || "",
            email: response.data.email || "",
            location_details: response.data.location_details || "",
            profile_image: response.data.profile_image || null,
          });

          const existingLinks = response.data.social_media_links || [];
          
          // Extract company website from social links (if exists)
          let website = "";
          const filteredLinks = existingLinks.filter(link => {
            if (link.includes('http') && 
                !link.includes('google.com/maps') && 
                !link.includes('maps.app.goo.gl') &&
                !link.includes('facebook.com') &&
                !link.includes('twitter.com') &&
                !link.includes('linkedin.com') &&
                !link.includes('instagram.com') &&
                !link.includes('github.com') &&
                !link.includes('whatsapp.com')) {
              website = link;
              return false;
            }
            return true;
          });

          setCompanyWebsite(website);
          
          const initialLinks = [...filteredLinks, ...Array(3 - filteredLinks.length).fill("")].slice(0, 3);
          setSocialLinks(initialLinks);

          const apiCardType = response.data.card_type?.toLowerCase() || "private";
          setSelectedCardType(apiCardType);
          
          if (response.data.profile_image) {
            setUploadedImage(response.data.profile_image);
          }

          setInitialDataLoaded(true);
        } catch (error) {
          console.error("Error fetching card details:", error);
          setError("Failed to load card details");
        }
      };
      
      fetchCardDetails();
    }
  }, [editMode, id, initialDataLoaded, navigate]);

  // Handle file upload for profile image
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
      };
      reader.readAsDataURL(file);
      setFormData({ ...formData, profile_image: file });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSocialLinkChange = (index, value) => {
    const newSocialLinks = [...socialLinks];
    newSocialLinks[index] = value;
    setSocialLinks(newSocialLinks);
  };

  const handleCompanyWebsiteChange = (value) => {
    setCompanyWebsite(value);
  };

  const removeSocialLink = (index) => {
    const newSocialLinks = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(newSocialLinks);
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, ""]);
  };

  // Update the social link rendering to show appropriate icons
  const getSocialIconClass = (link, index) => {
    if (isLocationLink(link)) {
      return "fas fa-map-marker-alt";
    }
    if (isWebsiteLink(link)) {
      return "fas fa-globe";
    }
    return index < socialMediaIcons.length ? socialMediaIcons[index].iconClass : "fas fa-link";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isOfflineMode()) {
      setError("You are offline. Creating or updating cards is disabled in offline mode.");
      return;
    }

    setIsLoading(true);
    setError(null);
  
    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        setError("Access token not found. Please log in again.");
        navigate("/login");
        return;
      }
  
      // Debug logs
      console.log("Card type being sent:", selectedCardType, "as", selectedCardType === "public" ? 1 : 2);
  
      const formDataPayload = new FormData();
      formDataPayload.append("full_name", formData.full_name);
      formDataPayload.append("designation", formData.designation);
      formDataPayload.append("company_name", formData.company_name);
      formDataPayload.append("phone_number", formData.phone_number);
      formDataPayload.append("email", formData.email);
      formDataPayload.append("location_details", formData.location_details);
      formDataPayload.append("card_type", selectedCardType === "public" ? 1 : 2);
  
      // Combine company website with social links
      let allLinks = [...socialLinks];
      if (companyWebsite.trim() !== "") {
        allLinks.push(companyWebsite);
      }
      
      const filteredSocialLinks = allLinks.filter(link => link.trim() !== "");
      formDataPayload.append("social_media_links", JSON.stringify(filteredSocialLinks));
      
      if (!editMode) {
        const fileId = sessionStorage.getItem("uploadedFileId");
        if (!fileId) {
          setError("File ID not found. Please upload a file first.");
          return;
        }
        formDataPayload.append("file", fileId);
      }
      
      if (formData.profile_image instanceof File) {
        formDataPayload.append("profile_image", formData.profile_image);
      }
  
      const url = editMode 
        ? `https://api.nexcard.co.in/api/cards/${id}/`
        : "https://api.nexcard.co.in/api/cards/";
      
      const method = editMode ? "patch" : "post";
  
      // Debug: Log the payload
      for (let [key, value] of formDataPayload.entries()) {
        console.log(key, value);
      }
  
      const response = await axios[method](
        url,
        formDataPayload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
  
      console.log("Card submission response:", response.data);
      
      // Force a refresh of the card list
      const redirectPath = location.state?.afterAddRedirect || "/home-my-cards";
      navigate(redirectPath, { replace: true });
      
    } catch (error) {
      console.error("Card submission failed:", error);
      setError(error.response?.data?.message || "Card submission failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleClick = (message) => {
    alert(message);
  };

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />
      <Header toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} />

      <div className="add-card-container3">
        <h2 className="add-card-title3">{editMode ? "Edit Card" : "Add New Card"}</h2>

        <div className="steps-container2">
          <div className="step3">
            <span className="step-number2">1</span>
            <div className="step-label2">
              Upload/Scan <br /> Business card
            </div>
          </div>

          <div className="line"></div>

          <div className="step3">
            <span className="step-number2">2</span>
            <div className="step-label2">
              Submit <br /> Profile Details
            </div>
          </div>
        </div>

        <div className="upload-form-container3">
          <div className="upload-box3">
            <label htmlFor="file-upload" className="upload-label3">
              {uploadedImage ? (
                <img src={uploadedImage} alt="Profile" className="uploaded-img3" />
              ) : (
                <>
                  <span className="x">+</span>
                  <p>Click to choose files</p>
                  <p className="file-size3">Max file size: 2MB</p>
                </>
              )}
            </label>
            <input 
              id="file-upload" 
              type="file" 
              accept=".jpg,.jpeg,.png" 
              onChange={handleFileChange} 
              hidden 
            />
          </div>

          <div className="form-right-section3">
            <div className="form-group3">
              <label>Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group3">
              <label>Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group3">
              <label>Company / Organisation Name</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        <p className="add-photo-text">Add Your Photo</p>

        <form className="card-form3" onSubmit={handleSubmit}>
          <div className="form-row3">
            <div className="left-section3">
              <div className="form-group3">
                <label>Phone Number</label>
                <div className="phone-input3">
                  <select>
                    <option value="+91">+91</option>
                    <option value="+1">+1</option>
                  </select>
                  <input
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group3">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="right-section3">
              <div className="form-group3">
                <label>Address Details</label>
                <textarea
                  name="location_details"
                  placeholder="Add your Office Address here"
                  value={formData.location_details}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>
          </div>

          {/* Company Website Section */}
          <div className="company-website-section">
            <label className="w">Company Website</label>
            <div className="social-link3">
              <div className="input-with-icon">
                <i className="fas fa-globe input-icon"></i>
                <input
                  type="text"
                  value={companyWebsite}
                  onChange={(e) => handleCompanyWebsiteChange(e.target.value)}
                  placeholder="https://www.companywebsite.com"
                />
              </div>
              <button type="button" className="save-btn3">
                <span>{companyWebsite ? "Saved" : "Save Link"}</span>
              </button>
            </div>
          </div>

          {/* Location Section */}
          <div className="location-section">
            <label className="w">Add Location on Map</label>
            <button 
              type="button" 
              className="add-location-btn"
              onClick={() => setShowLocationPicker(true)}
            >
              <i className="fas fa-map-marker-alt"></i>
              Add Location on Map
            </button>
            <p className="location-help-text">
              Click to select your location on the map
            </p>
          </div>

          <label className="w">Add Social Media Links</label>
          <button type="button" className="add-more3" onClick={addSocialLink}>
            + Add more
          </button>

          {socialLinks.map((link, index) => (
            <div key={index} className="social-link3">
              <div className="input-with-icon">
                <i className={`${getSocialIconClass(link, index)} input-icon`}></i>
                <input
                  type="text"
                  value={link}
                  onChange={(e) => handleSocialLinkChange(index, e.target.value)}
                  placeholder={isLocationLink(link) ? "Google Maps Location" : "Social media link"}
                />
              </div>

              <button
                type="button"
                className="delete-btn3"
                onClick={() => removeSocialLink(index)}
              >
                <span>Delete</span>
              </button>

              <button type="button" className="save-btn3">
                <span>{link ? "Saved" : "Save Link"}</span>
              </button>
            </div>
          ))}
          
          {error && <p className="error-message">{error}</p>}

          <div className="card-type3">
            <label className="custom-radio">
              <input
                type="radio"
                name="cardType"
                value="public"
                checked={selectedCardType === "public"}
                onChange={() => setSelectedCardType("public")}
              />
              <span className="radio-circle"></span>
              <img src={publicicon} alt="Public Card" className="radio-image" />
              Public Card
            </label>

            <label className="custom-radio">
              <input
                type="radio"
                name="cardType"
                value="private"
                checked={selectedCardType === "private"}
                onChange={() => setSelectedCardType("private")}
              />
              <span className="radio-circle"></span>
              <img src={privateicon} alt="Private Card" className="radio-image" />
              Private Card
            </label>
          </div>

          <div className="button-group2">
            <button className="cancel-btn2" type="button" onClick={handleBack}>
              Back to Upload Card
            </button>
            <button type="submit" className="save-btn2" disabled={isLoading}>
              {isLoading ? (editMode ? "Updating..." : "Submitting...") : (editMode ? "Update" : "Submit")}
            </button>
          </div>
        </form>

        {/* Location Picker Modal */}
        {showLocationPicker && (
          <LocationPicker 
            onLocationSelect={handleLocationSelect}
            onClose={() => setShowLocationPicker(false)}
          />
        )}
      </div>
      <Footer handleClick={handleClick} />
    </>
  );
};

export default AddNewCard2;